let audioCtx = null;
const SILENT_GAIN = 0.0001;
const MIN_FREQUENCY = 220;

function getAudioContext() {
  if (typeof window === 'undefined') return null;

  if (audioCtx && audioCtx.state === 'closed') {
    audioCtx = null;
  }

  if (!audioCtx) {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return null;

    audioCtx = new AudioContextClass();
  }

  return audioCtx;
}

function createTone(ctx, destination, options) {
  const {
    frequency,
    start,
    duration,
    type = 'sine',
    peak = 0.2,
    detune = 0,
    pan = 0,
    slideTo = null,
    attack = 0.025,
    release = duration,
  } = options;

  const oscillator = ctx.createOscillator();
  const gain = ctx.createGain();

  oscillator.type = type;
  oscillator.frequency.setValueAtTime(frequency, ctx.currentTime + start);
  oscillator.detune.setValueAtTime(detune, ctx.currentTime + start);

  if (slideTo) {
    oscillator.frequency.exponentialRampToValueAtTime(
      Math.max(MIN_FREQUENCY, slideTo),
      ctx.currentTime + start + duration
    );
  }

  gain.gain.setValueAtTime(SILENT_GAIN, ctx.currentTime + start);
  gain.gain.linearRampToValueAtTime(peak, ctx.currentTime + start + attack);
  gain.gain.exponentialRampToValueAtTime(
    SILENT_GAIN,
    ctx.currentTime + start + Math.max(attack + 0.03, release)
  );

  let output = gain;

  if (ctx.createStereoPanner) {
    const panner = ctx.createStereoPanner();
    panner.pan.setValueAtTime(pan, ctx.currentTime + start);
    gain.connect(panner);
    output = panner;
  }

  oscillator.connect(gain);
  output.connect(destination);

  oscillator.start(ctx.currentTime + start);
  oscillator.stop(ctx.currentTime + start + duration + 0.05);
}

export function playNotificationSound() {
  try {
    const ctx = getAudioContext();
    if (!ctx) return;

    const play = () => {
      const now = ctx.currentTime;

      const masterGain = ctx.createGain();
      masterGain.gain.setValueAtTime(SILENT_GAIN, now);
      masterGain.gain.linearRampToValueAtTime(1.32, now + 0.016);
      masterGain.gain.exponentialRampToValueAtTime(SILENT_GAIN, now + 1.65);

      const compressor = ctx.createDynamicsCompressor();
      compressor.threshold.setValueAtTime(-24, now);
      compressor.knee.setValueAtTime(30, now);
      compressor.ratio.setValueAtTime(7, now);
      compressor.attack.setValueAtTime(0.0015, now);
      compressor.release.setValueAtTime(0.2, now);

      const highPass = ctx.createBiquadFilter();
      highPass.type = 'highpass';
      highPass.frequency.setValueAtTime(180, now);
      highPass.Q.setValueAtTime(0.6, now);

      const lowPass = ctx.createBiquadFilter();
      lowPass.type = 'lowpass';
      lowPass.frequency.setValueAtTime(4800, now);
      lowPass.Q.setValueAtTime(0.9, now);

      const delay = ctx.createDelay();
      delay.delayTime.setValueAtTime(0.15, now);

      const feedback = ctx.createGain();
      feedback.gain.setValueAtTime(0.16, now);

      const delayGain = ctx.createGain();
      delayGain.gain.setValueAtTime(0.2, now);

      masterGain.connect(highPass);
      highPass.connect(lowPass);
      lowPass.connect(compressor);
      compressor.connect(ctx.destination);

      masterGain.connect(delay);
      delay.connect(feedback);
      feedback.connect(delay);
      delay.connect(delayGain);
      delayGain.connect(compressor);

      const destination = masterGain;

      // Bright opening pair that cuts through small phone speakers.
      createTone(ctx, destination, {
        frequency: 1318.51,
        start: 0,
        duration: 0.26,
        type: 'triangle',
        peak: 0.33,
        detune: -6,
        pan: -0.16,
        attack: 0.012,
        release: 0.24,
        slideTo: 1174.66,
      });

      createTone(ctx, destination, {
        frequency: 1567.98,
        start: 0.02,
        duration: 0.22,
        type: 'sine',
        peak: 0.18,
        detune: 8,
        pan: 0.18,
        attack: 0.01,
        release: 0.2,
        slideTo: 1318.51,
      });

      // Main body note with a gentle downward glide.
      createTone(ctx, destination, {
        frequency: 987.77,
        start: 0.14,
        duration: 0.68,
        type: 'sine',
        peak: 0.36,
        pan: -0.03,
        attack: 0.03,
        release: 0.62,
        slideTo: 783.99,
      });

      // Low support keeps the tone warm without turning muddy.
      createTone(ctx, destination, {
        frequency: 493.88,
        start: 0.16,
        duration: 0.86,
        type: 'triangle',
        peak: 0.22,
        pan: 0,
        attack: 0.04,
        release: 0.78,
        slideTo: 392,
      });

      // Mid shimmer extends the ring so it feels more like a chime than a beep.
      createTone(ctx, destination, {
        frequency: 1174.66,
        start: 0.3,
        duration: 0.52,
        type: 'sine',
        peak: 0.16,
        pan: 0.12,
        attack: 0.025,
        release: 0.48,
        slideTo: 987.77,
      });

      // Airy tail for perceived length at lower volume.
      createTone(ctx, destination, {
        frequency: 1760,
        start: 0.42,
        duration: 0.42,
        type: 'triangle',
        peak: 0.09,
        detune: 4,
        pan: -0.08,
        attack: 0.02,
        release: 0.38,
        slideTo: 1396.91,
      });
    };

    if (ctx.state === 'suspended') {
      ctx
        .resume()
        .then(play)
        .catch(() => {});
    } else {
      play();
    }
  } catch {
    // Audio is best-effort only.
  }
}

// Optional: call this once after a user click/tap to unlock audio on mobile browsers.
export function primeNotificationSound() {
  try {
    const ctx = getAudioContext();
    if (ctx && ctx.state === 'suspended') {
      ctx.resume().catch(() => {});
    }
  } catch {
    // Audio is best-effort only.
  }
}
